import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Header from '../components/Header';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { submitComplaint } from '../services/api';
import { useStores } from '../contexts/StoresContext';

function BadPage() {
  const { language, setLanguage } = useLanguage();
  const [feedback, setFeedback] = useState('');
  const { storeCode } = useParams<{ storeCode: string }>();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+7');
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const { isValid, get } = useStores();

  // Проверяем валидность кода магазина
  if (!storeCode || !isValid(storeCode)) return <Navigate to="/" replace />;

  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB в байтах
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB в байтах
  const MAX_IMAGES = 5;

  const store = get(storeCode)!;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Проверяем, что номер телефона содержит 12 цифр (включая код +7)
    const phoneDigits = phone.replace(/[^\d+]/g, '');
    if (phoneDigits.length !== 12) {
      setPhoneError(
        language === 'ru'
          ? 'Пожалуйста, введите полный номер телефона'
          : 'Толық телефон нөмірін енгізіңіз'
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setPhoneError(null);

    try {
      await submitComplaint({
        store_code: store.code,
        description: feedback,
        name,
        phone_number: phone,
        files,
      });
      
      setSubmitSuccess(true);
      // Очищаем форму
      setFeedback('');
      setName('');
      setPhone('+7');
      setFiles([]);
    } catch (error) {
      if (error && typeof error === 'object' && 'detail' in error) {
        setSubmitError((error as { detail: string }).detail);
      } else {
        setSubmitError(
          language === 'ru'
            ? 'Произошла ошибка при отправке. Пожалуйста, попробуйте позже.'
            : 'Жіберу кезінде қате пайда болды. Кейінірек қайталап көріңіз.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneError(null);
    
    // Всегда начинается с +7
    if (!value.startsWith('+7')) {
      setPhone('+7');
      return;
    }

    // Удаляем все кроме цифр и +
    const cleaned = value.replace(/[^\d+]/g, '');
    
    // Форматируем номер
    if (cleaned.length <= 2) {
      setPhone(cleaned);
    } else if (cleaned.length <= 5) {
      setPhone(`${cleaned.slice(0, 2)} (${cleaned.slice(2)}`);
    } else if (cleaned.length <= 8) {
      setPhone(`${cleaned.slice(0, 2)} (${cleaned.slice(2, 5)}) ${cleaned.slice(5)}`);
    } else if (cleaned.length <= 10) {
      setPhone(`${cleaned.slice(0, 2)} (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`);
    } else if (cleaned.length <= 12) {
      setPhone(`${cleaned.slice(0, 2)} (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8, 10)}-${cleaned.slice(10, 12)}`);
    }
  };

  const isImage = (file: File) => file.type.startsWith('image/');
  const isVideo = (file: File) => file.type.startsWith('video/');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const newFiles = Array.from(e.target.files);
    const imageFiles = newFiles.filter(isImage);
    const videoFiles = newFiles.filter(isVideo);
    const currentImages = files.filter(isImage);

    // Проверяем ограничения
    let errorMessage = '';

    // Проверка количества изображений
    if (currentImages.length + imageFiles.length > MAX_IMAGES) {
      errorMessage = language === 'ru' 
        ? `Можно загрузить не более ${MAX_IMAGES} изображений`
        : `${MAX_IMAGES} суреттен артық жүктей алмайсыз`;
    }

    // Проверка размера файлов
    for (const file of newFiles) {
      if (isImage(file) && file.size > MAX_IMAGE_SIZE) {
        errorMessage = language === 'ru'
          ? 'Размер изображения не должен превышать 10МБ'
          : 'Сурет көлемі 10МБ-тан аспауы керек';
        break;
      }
      if (isVideo(file) && file.size > MAX_VIDEO_SIZE) {
        errorMessage = language === 'ru'
          ? 'Размер видео не должен превышать 50МБ'
          : 'Бейне көлемі 50МБ-тан аспауы керек';
        break;
      }
    }

    if (errorMessage) {
      alert(errorMessage);
      e.target.value = ''; // Сбрасываем input
      return;
    }

    // Добавляем новые файлы к существующим
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
    e.target.value = ''; // Сбрасываем input для возможности повторной загрузки тех же файлов
  };

  const removeFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const formatFileSize = (size: number) => {
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} КБ`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
  };

  return (
    <div className="App">
      <Header />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      <main className="main-content">
        <div className="bad-header">
          <img src="/bad.png" className="mascot" alt="Qulpynai" />
        </div>

        {submitSuccess ? (
          <div className="success-message">
            <h2>
              {language === 'ru'
                ? 'Спасибо за ваш отзыв!'
                : 'Пікіріңіз үшін рахмет!'}
            </h2>
            <p>
              {language === 'ru'
                ? 'Мы обязательно рассмотрим вашу жалобу и примем необходимые меры.'
                : 'Біз міндетті түрде сіздің шағымыңызды қарастырып, қажетті шараларды қабылдаймыз.'}
            </p>
            <button
              className="button primary"
              onClick={() => setSubmitSuccess(false)}
            >
              {language === 'ru' ? 'Отправить еще' : 'Тағы жіберу'}
            </button>
          </div>
        ) : (
          <>
          <h1 style={{textAlign: 'center', fontFamily: 'monospace'}}  >
          {language === 'ru'
                ? 'Просим Прощения!'
                : 'Кешірім Сұрамыз!'}
          </h1>
            <h1 className="question">
              {language === 'ru'
                ? 'Пожалуйста напишите что Вам не понравилось, мы обязательно проверим этот факт и постараемся исправиться и работать лучше!'
                : 'Сізге не ұнамағанын жазыңыз, біз бұл жағдайды міндетті түрде қарастырып, тексереміз және қызметімізді жақсарту үшін барлық қажетті шараларды қолданамыз!'}
            </h1>

            {submitError && (
              <div className="error-message">
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="form-group">
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={language === 'ru' ? 'Ваш отзыв' : 'Сіздің пікіріңіз'}
                  rows={4}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label className="file-upload">
                  <span>{language === 'ru' ? 'Загрузить файл' : 'Файл жүктеу'}</span>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*,video/*"
                    multiple
                    style={{ display: 'none' }}
                    disabled={isSubmitting}
                  />
                </label>
                <div className="files-list">
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">({formatFileSize(file.size)})</span>
                      <button
                        type="button"
                        className="file-remove"
                        onClick={() => removeFile(index)}
                        disabled={isSubmitting}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                {files.length > 0 && (
                  <div className="file-limit-info">
                    {language === 'ru'
                      ? `Загружено ${files.filter(isImage).length}/${MAX_IMAGES} изображений`
                      : `${files.filter(isImage).length}/${MAX_IMAGES} сурет жүктелді`}
                  </div>
                )}
              </div>

              <div className="form-group">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={language === 'ru' ? 'Имя' : 'Аты'}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="+7 (777) 777-77-77"
                  required
                  disabled={isSubmitting}
                />
                {phoneError && (
                  <div className="error-message">
                    {phoneError}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="button primary"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? (language === 'ru' ? 'Отправка...' : 'Жіберілуде...')
                  : (language === 'ru' ? 'Отправить' : 'Жіберу')}
              </button>

               <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', textAlign: 'center' }}>
                {language === 'ru'
                  ? 'Нажимая кнопку «Отправить», вы соглашаетесь с политикой обработки персональных данных'
                  : '«Жіберу» түймесін басу арқылы сіз дербес деректерді өңдеу саясатымен келісесіз'}
              </p>

            </form>
          </>
        )}
      </main>
    </div>
  );
}

export default BadPage; 